import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Building, FileText, Upload, CheckCircle, AlertCircle, Loader, Paperclip, X, PlusCircle } from 'lucide-react';
import Button from '../../components/common/Button';
import authService from '../../api/authService';
import jobSearchApi from '../../api/jobSearch';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as Sentry from '@sentry/react';
import ExpiredLicenseBanner from '../../components/professional/ExpiredLicenseBanner';

const documentTypes = [
    { value: 'cv', label: 'Resume / CV' },
    { value: 'cover_letter', label: 'Cover Letter' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'license', label: 'License' },
    { value: 'reference', label: 'Reference Letter' },
    { value: 'portfolio', label: 'Portfolio' },
    { value: 'other', label: 'Other Document' },
];

const JobApplicationForm = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [coverLetter, setCoverLetter] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState('');
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const fileInputRef = useRef(null);


    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                const response = await jobSearchApi.getJobDetails(jobId);
                const jobData = response.data;
                setJob(jobData);
                const isJobExpired = Boolean(
                    jobData?.is_expired ||
                    (jobData?.expiry_date && new Date(jobData.expiry_date) < new Date()) ||
                    jobData?.status === 'expired'
                );
                if (isJobExpired) {
                    setError('This job posting has expired and is no longer accepting applications.');
                }
            } catch (err) {
                setError('Failed to load job details. The job may no longer be available.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchJobDetails();
    }, [jobId]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const newDocs = files.map(file => ({ file, type: 'cv' })); // Default to CV
            setDocuments(prev => [...prev, ...newDocs]);
        }
    };

    const handleDocTypeChange = (index, type) => {
        setDocuments(prev => prev.map((doc, i) => i === index ? { ...doc, type } : doc));
    };

    const handleOtherDocTypeChange = (index, otherType) => {
        setDocuments(prev => prev.map((doc, i) => i === index ? { ...doc, otherType } : doc));
    };

    const removeDocument = (index) => {
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        setSuccess('');

        if (coverLetter.length < 100) {
            setError('Your cover letter must be at least 100 characters long.');
            return;
        }

        if (!disclaimerAccepted) {
            setError('You must accept the profile visibility disclaimer to apply.');
            setIsSubmitting(false);
            return;
        }

        try {
            const applicationData = {
                jobId: job.id,
                coverLetter,
                documents,
                answers: { additional_information: additionalInfo },
                disclaimerAccepted,
            };
            await authService.applyForJob(applicationData);
            setSuccess('Application submitted successfully! Redirecting...');
            setTimeout(() => navigate('/my-applications'), 2000);
        } catch (err) {
            console.error("Application submission error", err);
            const errorResponse = err.response?.data;
            Sentry.captureException(err, "Job application submission error")

            // Handle profile verification error specifically
            if (errorResponse?.error === 'Profile verification incomplete') {
                const missingDocs = errorResponse.missing_documents || [];
                const verificationPercentage = errorResponse.verification_percentage || 0;

                const errorMsg = (
                    <div className="text-left">
                        <p className="font-semibold mb-2">{errorResponse.message}</p>
                        <p className="text-sm mb-2">Verification Progress: {verificationPercentage}%</p>
                        <p className="text-sm mb-2">Missing documents:</p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                            {missingDocs.map((doc, index) => (
                                <li key={index} className="capitalize">{doc.replace('_', ' ')}</li>
                            ))}
                        </ul>
                        <Button
                            onClick={() => navigate('/manage-profile')}
                            className="mt-3"
                            variant="primary"
                            size="sm"
                        >
                            Complete Profile
                        </Button>
                    </div>
                );

                toast.error(
                    <div>
                        <p className="font-semibold">Verification Required</p>
                        <p className="text-sm">Please complete your profile verification</p>
                    </div>
                );
                setError(errorMsg);
                return;
            }

            // Handle other error types
            let specificError = null;

            if (errorResponse?.message && errorResponse.message !== 'Failed to create job application') {
                specificError = errorResponse.message;
            } else if (errorResponse?.job) {
                specificError = Array.isArray(errorResponse.job) ? errorResponse.job[0] : String(errorResponse.job);
            } else if (errorResponse?.non_field_errors) {
                specificError = Array.isArray(errorResponse.non_field_errors) ? errorResponse.non_field_errors[0] : String(errorResponse.non_field_errors);
            } else if (errorResponse?.details || errorResponse?.detail) {
                const details = errorResponse.details || errorResponse.detail;
                if (typeof details === 'string') {
                    // Match Python ErrorDetail string e.g. [ErrorDetail(string='This job posting has expired.', ...)]
                    const errorDetailMatch = details.match(/ErrorDetail\(string=['"]([^'"]+)['"]/);
                    if (errorDetailMatch && errorDetailMatch[1]) {
                        specificError = errorDetailMatch[1];
                    } else {
                        const dictMatch = details.match(/:\s*\[?['"]([^'"]+)['"]/);
                        if (dictMatch && dictMatch[1]) {
                            specificError = dictMatch[1];
                        } else if (!details.startsWith('{') && details.trim()) {
                            specificError = details;
                        }
                    }
                } else if (typeof details === 'object' && details !== null) {
                    if (details.job) {
                        specificError = Array.isArray(details.job) ? details.job[0] : String(details.job);
                    } else {
                        const firstKey = Object.keys(details)[0];
                        if (firstKey) {
                            const val = details[firstKey];
                            specificError = Array.isArray(val) ? val[0] : String(val);
                        }
                    }
                }
            }

            const mainError = errorResponse?.error || 'Failed to create job application';
            const errorDetail = specificError || errorResponse?.detail || 'An unexpected error occurred during application.';
            const isExpired = String(errorDetail).toLowerCase().includes('expired') || String(mainError).toLowerCase().includes('expired');

            // Show error popup modal
            Swal.fire({
                icon: 'error',
                title: mainError,
                text: errorDetail,
                confirmButtonColor: '#0284c7',
                confirmButtonText: isExpired ? 'Browse Available Jobs' : 'OK',
                showCancelButton: isExpired,
                cancelButtonText: 'Stay on Page'
            }).then((result) => {
                if (result.isConfirmed && isExpired) {
                    navigate('/job-search');
                }
            });

            const combinedMsg = mainError && errorDetail && mainError !== errorDetail
                ? `${mainError}: ${errorDetail}`
                : (errorDetail || mainError);

            toast.error(combinedMsg);
            setError(combinedMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div className="flex items-center justify-center min-h-[calc(100vh-160px)]"><Loader className="w-12 h-12 text-primary-500 animate-spin" /></div>;
    }

    if (error && !job) {
        return (
            <div className="container mx-auto py-12 px-4 text-center">
                <div className="bg-white p-8 rounded-2xl shadow-lg border max-w-md mx-auto">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-secondary-800">Application Error</h2>
                    <p className="text-red-600 mt-2">{error}</p>
                    <Button onClick={() => navigate('/job-search')} className="mt-6">Back to Jobs</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-secondary-50">
            <div className="container mx-auto py-8 px-4">
                <Button variant="link" onClick={() => navigate(-1)} className="mb-6 text-secondary-600">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Job Details
                </Button>

                <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-gray-100">
                    <div className="flex items-start gap-6 mb-6 pb-6 border-b">
                        <div className="w-16 h-16 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-8 h-8 text-primary-600" />
                        </div>
                        <div>
                            <p className="text-primary-600 font-semibold mb-1">Applying for</p>
                            <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">{job.title}</h1>
                            <div className="flex items-center text-secondary-600 mt-2">
                                <Building className="w-4 h-4 mr-2" /> {job.company_name || 'Aviation Company'}
                            </div>
                        </div>
                    </div>

                    {/* License expiry warning – informational only, never blocks submission */}
                    <ExpiredLicenseBanner />

                    {success && (
                        <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-lg flex items-center gap-3 border border-green-200">
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                            <span className="text-sm font-medium">{success}</span>
                        </div>
                    )}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg border border-red-200">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    {typeof error === 'string' ? (
                                        <span className="text-sm font-medium">{error}</span>
                                    ) : (
                                        error
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-8 sentry-mask">
                        <div>
                            <label className="text-lg font-semibold text-secondary-800 mb-2 block">Cover Letter</label>
                            <textarea
                                value={coverLetter}
                                onChange={(e) => setCoverLetter(e.target.value)}
                                rows="8"
                                className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                placeholder="Write a compelling cover letter explaining why you are the best fit for this role..."
                                required
                            />
                            <p className="text-xs text-secondary-500 mt-2">Minimum 100 characters. Current: {coverLetter.length}</p>
                        </div>

                        <div>
                            <label className="text-lg font-semibold text-secondary-800 mb-2 block">Supporting Documents</label>
                            <div className="space-y-3">
                                {documents.map((doc, index) => (
                                    <div key={index} className="flex flex-col sm:flex-row items-center gap-3 p-3 bg-secondary-50 rounded-lg border">
                                        <Paperclip className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                                        <p className="text-sm font-medium text-secondary-800 flex-1 truncate">{doc.file.name}</p>
                                        <select value={doc.type} onChange={(e) => handleDocTypeChange(index, e.target.value)} className="p-2 border border-secondary-300 rounded-md bg-white text-sm">
                                            {documentTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                        {doc.type === 'other' && (
                                            <input
                                                type="text"
                                                placeholder="Specify document name"
                                                value={doc.otherType || ''}
                                                onChange={(e) => handleOtherDocTypeChange(index, e.target.value)}
                                                className="p-2 border border-secondary-300 rounded-md bg-white text-sm flex-1"
                                            />
                                        )}
                                        <Button type="button" variant="destructive" size="sm" className="p-2 h-9 w-9" onClick={() => removeDocument(index)}>
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <input type="file" multiple ref={fileInputRef} onChange={handleFileChange} className="hidden" accept='.pdf,.png,.jpg,.jpeg' />
                            <Button type="button" variant="outline" onClick={() => fileInputRef.current.click()} className="mt-4">
                                <PlusCircle className="w-4 h-4 mr-2" /> Add Documents
                            </Button>
                        </div>

                        <div>
                            <label className="text-lg font-semibold text-secondary-800 mb-2 block">Additional Information (Optional)</label>
                            <textarea
                                value={additionalInfo}
                                onChange={(e) => setAdditionalInfo(e.target.value)}
                                rows="4"
                                className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                placeholder="Is there anything else you'd like the hiring manager to know?"
                            />
                        </div>

                        <div className="bg-primary-50 p-4 rounded-xl border border-primary-100 flex items-start gap-3">
                            <input
                                type="checkbox"
                                id="disclaimer"
                                checked={disclaimerAccepted}
                                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                required
                            />
                            <label htmlFor="disclaimer" className="text-sm text-secondary-700 leading-relaxed">
                                I understand that by applying for this job, my professional profile (including experience, licenses, and qualifications) will become visible to the recruiter. I can revoke this access at any time by withdrawing my application.
                            </label>
                        </div>

                        <div className="pt-6 border-t flex justify-end">
                            <Button type="submit" size="lg" loading={isSubmitting} disabled={isSubmitting}>
                                Submit Application
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default JobApplicationForm;

